import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef } from 'react';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import AvatarChip from '../../../shared/avatar-chip';
import z from 'zod';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

interface ReassignAliasesActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}

export const ReassignAliasesActionSchema = z.object({
  type: z.literal('Reassign_Aliases'),
  destUser: z.string().min(1, 'A user is required.'),
});

export type ReassignAliasesActionType = z.infer<
  typeof ReassignAliasesActionSchema
>;

// Type guard to check if an action is ReassignAliasesActionType
function isReassignAliasesAction(
  action: PolicyAction
): action is ReassignAliasesActionType {
  return action.type === 'Reassign_Aliases';
}

export default function ReassignAliasesAction({
  setValue,
  watch,
  errors,
  control,
}: ReassignAliasesActionProps) {
  const autocompleteKey = useRef(new Date().getTime().toString());
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Reassign_Aliases');
  const actionAtIndex = actionData[idx];
  if (!isReassignAliasesAction(actionAtIndex)) {
    return null;
  }

  const currentAction: ReassignAliasesActionType = actionAtIndex;
  const error: FieldErrors<ReassignAliasesActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={2}>
        <Box sx={{ width: 400 }}>
          {currentAction.destUser && currentAction.destUser.length > 0 ? (
            <AvatarChip
              type="User"
              email={currentAction.destUser}
              onDelete={() => setValue(`actionData.${idx}.destUser`, '')}
              size="medium"
            />
          ) : (
            <Controller
              name={`actionData.${idx}.destUser`}
              control={control}
              render={() => (
                <UsersAutocomplete
                  newKey={`${autocompleteKey.current}`}
                  autocompleteValue={currentAction.destUser}
                  setAutoCompleteValue={(newValue) =>
                    setValue(`actionData.${idx}.destUser`, newValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  errors={!!error?.destUser}
                  helperText={error?.destUser?.message || ' '}
                />
              )}
            />
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
